import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { connect } from 'react-redux'
import {
  DEFAULT_LIMIT_PER_INDEX,
  SEARCH_PROFILES,
  buildMiniSearchIndices,
  miniSearchHasDocument,
  miniSearchIndexSizes,
  searchPerMiniSearchIndex,
} from '../local/buildMiniSearchIndices'
import { searchDebugLog } from '../searchDebugLog'

// Result-object keys, which mirror the Algolia index names.
// eslint-disable-next-line camelcase
const FIRST_PAGE = { repos: 1, folders: 1, sets_questions: 1 }
const INDEX_KEYS = Object.keys(FIRST_PAGE)

// An empty query still has to emit the full three-index shape. Algolia does,
// and consumers read `indexResults.repos.hitCount` without guarding — so
// emitting `undefined` here throws on mount the moment this provider takes the
// display role.
function emptyIndexResults() {
  return INDEX_KEYS.reduce((acc, key) => ({
    ...acc,
    [key]: {
      hits: [], hitCount: 0, hasMore: false, loadMore: () => {},
    },
  }), {})
}

// Which indexes this surface actually presents, in Algolia's order. The full
// three-index `indexResults` is always emitted for the quality report; `hits`
// and `suggestions` are the display projections, and they have to mirror
// Algolia exactly or the two engines render differently. On sectionHome, for
// instance, Algolia returns sets_questions alone — concatenating all three
// there would offer packs and folders as pollable items in add-to-class.
//
// Takes the comma-joined secondary list rather than the array so the caller can
// use it as a plain effect dependency.
function presentedIndexes(primaryIndex, secondaryKey) {
  const secondary = secondaryKey ? secondaryKey.split(',') : []
  return [primaryIndex, ...secondary]
}

/**
 * Headless search provider for local Redux-derived library content, powered by
 * MiniSearch (BM25 + token-level prefix/fuzzy).
 *
 * Two instances run at once, differing only in the `profile` prop: `baseline`
 * (today's live behaviour) under providerId='minisearch-content', and `tuned`
 * under providerId='minisearch-tuned-content'. Both feed searchReporter, which
 * scores each against Algolia on the same query.
 *
 * While Algolia holds the display role both instances are shadows and neither
 * is shown to anyone. When REACT_APP_SEARCH_DISPLAY_ENGINE flips to
 * `minisearch`, the baseline instance takes over the display role — which is
 * why this provider implements real paging rather than the stub a shadow could
 * get away with.
 *
 * Running both is cheap — they share the memoised indices, so the second is one
 * extra in-memory BM25 pass — and it makes the comparison paired: same query,
 * same library, same user, same moment. That is far stronger evidence than
 * comparing one week to the next in a product with a school calendar.
 *
 * Output shape mirrors AlgoliaSearchProvider exactly so the same
 * `IndexHitList` / `SectionHeader` primitives can render either engine
 * without branching:
 *   {
 *     suggestions: [{ index, hits }, ...],
 *     hits:        <flat list across all indices>,
 *     indexResults: {
 *       repos:          { hits, hitCount, hasMore, loadMore },
 *       folders:        { hits, hitCount, hasMore, loadMore },
 *       sets_questions: { hits, hitCount, hasMore, loadMore },
 *     }
 *   }
 *
 * Each hit copies the source Redux record and adds:
 *   - objectID:           Algolia-compatible primary key (= record id)
 *   - __localOrigin:      true marker so the UI can render a local badge
 *   - __localEngine:      'minisearch' — which local engine produced it
 *   - __minisearchProfile: 'baseline' | 'tuned' — which profile produced it
 *   - __minisearchScore:  BM25 score (higher = stronger match)
 *   - __minisearchTerms:  array of query terms that matched
 *   - __minisearchMatch:  per-term → matched-fields map (MiniSearch `match`)
 */
function MiniSearchContentProvider({
  query, options, onResults, profile,
  sets, questions, folders, repos,
}) {
  const indices = useMemo(
    () => buildMiniSearchIndices({
      sets, questions, folders, repos,
    }),
    [sets, questions, folders, repos],
  )

  const onResultsRef = useRef(onResults)
  onResultsRef.current = onResults
  const queryRef = useRef(query)
  queryRef.current = query

  const indexSizes = useMemo(() => miniSearchIndexSizes(indices), [indices])

  // Lets the reporter ask, per Algolia hit that this engine missed, whether the
  // document was in the local index at all. Emitted rather than exported so the
  // reporter never has to know how the indices were built or memoised.
  const hasDocument = useCallback(
    (indexName, documentType, objectID) => (
      miniSearchHasDocument(indices, indexName, documentType, objectID)
    ),
    [indices],
  )

  // How many pages deep each index is currently showing, held together with the
  // query it applies to so a new query resets paging without a second effect
  // (which would emit results twice for every keystroke).
  const [paging, setPaging] = useState({ query: null, pages: FIRST_PAGE })
  const pages = paging.query === query ? paging.pages : FIRST_PAGE

  // Show more raises one index's cap. Everything is already in memory, so this
  // re-slices rather than fetching.
  const loadMoreForIndex = useCallback((indexKey) => {
    setPaging((prev) => {
      const current = prev.query === queryRef.current ? prev.pages : FIRST_PAGE
      return {
        query: queryRef.current,
        pages: { ...current, [indexKey]: current[indexKey] + 1 },
      }
    })
  }, [])

  const perPage = (options && options.hitsPerPage) || DEFAULT_LIMIT_PER_INDEX
  const limits = useMemo(
    () => INDEX_KEYS.reduce(
      (acc, key) => ({ ...acc, [key]: perPage * pages[key] }),
      {},
    ),
    [perPage, pages],
  )

  const primaryIndex = (options && options.primaryIndex) || 'repos'
  const secondaryKey = ((options && options.secondaryIndexes) || []).join(',')

  useEffect(() => {
    const presented = presentedIndexes(primaryIndex, secondaryKey)
    if (!query) {
      onResultsRef.current({
        query,
        suggestions: [],
        hits: [],
        indexResults: emptyIndexResults(),
        indexSizes,
        hasDocument,
      })
      return
    }
    const found = searchPerMiniSearchIndex(indices, query, limits, profile)
    const indexResults = INDEX_KEYS.reduce((acc, key) => ({
      ...acc,
      [key]: { ...found[key], loadMore: () => loadMoreForIndex(key) },
    }), {})
    // Primary index only, matching Algolia — the flat list feeds the
    // add-to-class modal, where a hit from another index is not a valid pick.
    const primaryHits = (indexResults[presented[0]] || { hits: [] }).hits
    const suggestions = presented.map((key) => ({
      index: key,
      hits: (indexResults[key] || { hits: [] }).hits,
    }))
    // MiniSearch is synchronous, so the emitted `query` always equals the
    // query prop. It is reported anyway to keep the provider contract
    // identical to Algolia's, which lets either engine take the display role.
    onResultsRef.current({
      query,
      suggestions,
      hits: primaryHits,
      indexResults,
      indexSizes,
      hasDocument,
    })
    searchDebugLog(`[local-search:minisearch-content:${profile || SEARCH_PROFILES.BASELINE}]`, {
      query,
      limits,
      profile,
      presented,
      hitCount: primaryHits.length,
      indexSizes,
      indexResults,
      hits: primaryHits,
    })
  }, [
    query, indices, indexSizes, hasDocument, limits, loadMoreForIndex, profile,
    primaryIndex, secondaryKey,
  ])

  return null
}

function mapStateToProps(state) {
  return {
    sets: state.sets,
    questions: state.questions,
    folders: state.folders,
    repos: state.repos,
  }
}

export default connect(mapStateToProps)(MiniSearchContentProvider)
